import React, { useEffect, useState } from "react";
import { useT } from "../i18n/I18nProvider";

function CardCarousel({ images, alt }) {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (!images || images.length <= 1) return;
    const id = setInterval(() => setIdx((i) => (i + 1) % images.length), 3500);
    return () => clearInterval(id);
  }, [images]);
  return (
    <div className="relative aspect-[16/10] bg-stone-100 overflow-hidden" data-testid="card-carousel">
      {images.map((src, i) => (
        <img
          key={src}
          src={src}
          alt={alt}
          loading="lazy"
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${i === idx ? "opacity-100" : "opacity-0"}`}
        />
      ))}
      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
        {images.map((_, i) => (
          <button
            key={i}
            type="button"
            aria-label={`Show image ${i + 1}`}
            data-testid={`carousel-dot-${i}`}
            onClick={() => setIdx(i)}
            className={`h-2 rounded-full transition-all ${i === idx ? "w-6 bg-white" : "w-2 bg-white/60 hover:bg-white/80"}`}
          />
        ))}
      </div>
    </div>
  );
}

const ICONS = {
  pottery: (
    <svg viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 12 H32 L34 18 C34 28 30 36 24 36 C18 36 14 28 14 18 Z" />
      <path d="M14 18 H34" />
      <path d="M20 24 q4 -4 8 0" />
      <circle cx="24" cy="14" r="1.5" fill="currentColor" />
    </svg>
  ),
  metal: (
    <svg viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="24" cy="24" r="6" />
      <path d="M24 6v6M24 36v6M6 24h6M36 24h6M11 11l4 4M33 33l4 4M37 11l-4 4M11 37l4-4" />
    </svg>
  ),
  statuary: (
    <svg viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M24 6 v8" />
      <path d="M14 14 H34 L31 22 H17 Z" />
      <path d="M16 22 v6 H32 v-6" />
      <path d="M12 30 H36 v6 H12 Z" />
    </svg>
  ),
  rustic: (
    <svg viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M8 18 H40 V24 H8 Z" />
      <path d="M12 24 V38" />
      <path d="M36 24 V38" />
      <path d="M14 18 V12 H34 V18" />
    </svg>
  ),
  iron: (
    <svg viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="24" cy="20" r="8" />
      <path d="M24 28 v12" />
      <path d="M14 40 H34" />
      <path d="M18 18 q6 -6 12 0" />
    </svg>
  ),
  wall: (
    <svg viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="24" cy="24" r="10" />
      <path d="M24 8v4M24 36v4M8 24h4M36 24h4M13 13l3 3M32 32l3 3M35 13l-3 3M13 35l3-3" />
    </svg>
  ),
};

const KEYS = [
  { key: "pottery", color: "#14532d", bg: "#e8efe9", img: "/elsol-storefront.jpg" },
  { key: "metal", color: "#b8420f", bg: "#fbeadd", img: "/elsol-metal-art.jpg" },
  { key: "statuary", color: "#14532d", bg: "#e8efe9", sideImg: "/elsol-fountain.jpg" },
  { key: "rustic", color: "#b8420f", bg: "#fbeadd", img: "/elsol-rustic.jpg" },
  { key: "iron", color: "#14532d", bg: "#e8efe9", img: "/elsol-iron.jpg" },
  { key: "wall", color: "#b8420f", bg: "#fbeadd", img: "/elsol-wall.jpg" },
];

export default function Categories() {
  const { t } = useT();
  return (
    <section id="categories" data-testid="categories-section" className="py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-5 md:px-10">
        <div className="grid md:grid-cols-12 gap-6 mb-14 items-end">
          <div className="md:col-span-7">
            <div className="text-[12px] uppercase tracking-[0.22em] text-[#b8420f] font-bold mb-3">
              {t("categories.eyebrow")}
            </div>
            <h2 className="font-display font-black text-stone-900 text-[38px] md:text-[52px] leading-[1.05] tracking-tight">
              {t("categories.h_a")}{" "}
              <span className="italic text-[#b8420f]">{t("categories.h_b")}</span>
            </h2>
          </div>
          <div className="md:col-span-5 text-stone-600 text-[16px]">{t("categories.sub")}</div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {KEYS.map((c) => (
            <article
              key={c.key}
              data-testid={`category-card-${c.key}`}
              className={`lift relative bg-white border border-stone-200/80 rounded-3xl overflow-hidden ${c.sideImg ? "flex flex-row" : "flex flex-col"}`}
            >
              {c.imgs ? (
                <CardCarousel images={c.imgs} alt={t(`categories.${c.key}_t`)} />
              ) : c.img ? (
                <div className="relative aspect-[16/10] bg-stone-100 overflow-hidden">
                  <img
                    src={c.img}
                    alt={t(`categories.${c.key}_t`)}
                    className="absolute inset-0 w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
              ) : c.sideImg ? (
                <div className="relative w-2/5 flex-shrink-0 bg-stone-100 overflow-hidden">
                  <img
                    src={c.sideImg}
                    alt={t(`categories.${c.key}_t`)}
                    className="absolute inset-0 w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
              ) : null}
              <div className="relative p-7 flex flex-col flex-1">
                <div className="absolute -top-10 -right-10 w-44 h-44 rounded-full opacity-60" style={{ background: c.bg }} aria-hidden="true" />
                <div className="relative w-12 h-12 rounded-2xl flex items-center justify-center mb-5" style={{ background: c.bg, color: c.color }}>
                  <div className="w-7 h-7">{ICONS[c.key]}</div>
                </div>
                <h3 className="relative font-display font-bold text-2xl text-stone-900 mb-2 leading-tight">
                  {t(`categories.${c.key}_t`)}
                </h3>
                <p className="relative text-[15px] text-stone-600 leading-relaxed">
                  {t(`categories.${c.key}_b`)}
                </p>
                <div className="relative mt-5 inline-flex items-center gap-1.5 text-[13px] font-bold" style={{ color: c.color }}>
                  {t("categories.in_store")}
                  <span className="w-1.5 h-1.5 rounded-full bg-current" />
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
